import React, { useEffect, useRef } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import type {
  ReviewEditBase,
  useUnifiedEngagementReview,
} from '../../../hooks/advisory/useUnifiedEngagementReview';
import {
  hasReviewPreparationFailure,
  type ReviewConflictField,
  type ReviewProgress,
} from '../../../hooks/advisory/advisoryReview';
import type { ReportingPeriodV01 } from '../../../hooks/advisory/advisoryReportingPeriod';
import { contentColumn, meta } from '../../../styles/visualLanguage';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { AdvisoryCalloutState } from './AdvisoryCalloutState';
import { AdvisoryReviewWarning } from './AdvisoryReviewWarning';
import { AdvisoryReviewRecoveryControls } from './AdvisoryReviewRecoveryControls';
import { AdvisoryReviewInitialPreparation } from './AdvisoryReviewInitialPreparation';
import {
  AdvisoryUnifiedReviewScreen,
  type AdvisoryReviewField,
  type AdvisoryReviewEditableField,
  type AdvisoryReviewSegment,
} from './AdvisoryUnifiedReviewScreen';

type Controller = ReturnType<typeof useUnifiedEngagementReview>;
const viewField = (field: ReviewConflictField): AdvisoryReviewField =>
  field === 'period' ? 'reportingPeriod' : field;
const wireField = (field: AdvisoryReviewField): ReviewConflictField =>
  field === 'reportingPeriod' ? 'period' : field;

interface LiveProps {
  review: Controller;
  initialQuestion?: string;
  progress?: ReviewProgress | null;
  animateEntry?: boolean;
  initialReadFailure?: 'denied' | 'failed';
  onReloadInitial?: () => void;
}
export function AdvisoryUnifiedReviewLive(props: LiveProps) {
  return <UnifiedReviewContent key={props.review.scope} {...props} />;
}

function UnifiedReviewContent({
  review,
  initialQuestion = '',
  progress,
  animateEntry,
  initialReadFailure,
  onReloadInitial,
}: LiveProps) {
  const { t } = useUntypedTranslation();
  const snapshot = review.snapshot;
  const isClarification = snapshot?.errors.some(error => error.code === 'clarificationRequired');
  const failedInitialExtraction =
    snapshot?.statement.segments.length === 0 && hasReviewPreparationFailure(snapshot);
  const displayedDraft = useRef(
    Boolean(
      snapshot &&
        review.values &&
        !failedInitialExtraction &&
        (snapshot.statement.segments.length > 0 ||
          (snapshot.status !== 'preparing' && (snapshot.canRefine || isClarification)))
    )
  );
  // Initial snapshots contain placeholder values. Even a ready stream snapshot
  // can arrive before the prepare request releases those values in the hook.
  // Once shown, keep the draft mounted through later preparation/refinement.
  const initialPreparation =
    !displayedDraft.current &&
    (failedInitialExtraction ||
      review.busy === 'prepare' ||
      (snapshot?.statement.segments.length === 0 &&
        (snapshot.status === 'preparing' || (!snapshot.canRefine && !isClarification))));
  useEffect(() => {
    if (snapshot && review.values && !initialPreparation && !review.approved)
      displayedDraft.current = true;
  }, [snapshot, review.values, review.approved, initialPreparation]);
  const editor = useRef<{ field: AdvisoryReviewEditableField; base: ReviewEditBase } | null>(null);
  if (editor.current && editor.current.base.scope !== review.scope) editor.current = null;
  const describe = (value: string | string[] | ReportingPeriodV01): string => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.join(', ');
    const basis = t(
      value.yearType === 'fiscal' ? 'advisory:period.fiscalShort' : 'advisory:period.calendarShort'
    );
    const range = value.quarter
      ? `Q${value.quarter}${
          value.endQuarter && value.endQuarter !== value.quarter ? `–Q${value.endQuarter}` : ''
        }`
      : t('advisory:period.rangeYear');
    const month =
      value.yearType === 'fiscal'
        ? ` · ${t('advisory:period.fiscalStartMonthLabel')}: ${t(
            `advisory:period.month${String(value.fiscalStartMonth).padStart(2, '0')}`
          )}`
        : '';
    return `${basis} ${value.year} ${range}${month}`;
  };
  const readFailed = review.readError;
  if (!snapshot || !review.values || review.approved || initialPreparation) {
    const failed = Boolean(review.error || snapshot?.downstream.status === 'failed');
    if (!review.approved) {
      return (
        <AdvisoryReviewInitialPreparation
          review={review}
          question={initialQuestion || snapshot?.originalQuery || ''}
          progressStage={
            progress && progress.revision === (snapshot?.revision ?? 1) ? progress.stage : undefined
          }
          animateEntry={animateEntry}
          readFailure={initialReadFailure}
          onReload={onReloadInitial}
        />
      );
    }
    return (
      <AdvisoryEngagementShell position="02" total={null} titleKey="advisory:review.title">
        {!readFailed && (
          <Typography
            role={failed ? 'alert' : 'status'}
            data-testid="advisory-review-loading"
            sx={[meta, { mt: 3 }]}
          >
            {t(
              failed
                ? 'advisory:review.operationFailed'
                : review.approved
                ? 'advisory:review.approvedPreparing'
                : 'advisory:review.preparing'
            )}
          </Typography>
        )}
        <AdvisoryReviewRecoveryControls review={review} />
      </AdvisoryEngagementShell>
    );
  }
  // After a draft has been displayed, an empty statement no longer selects the
  // initial view. Keep goal-listener recovery available on this path too.
  if (initialReadFailure) {
    return (
      <AdvisoryCalloutState
        tone={initialReadFailure === 'denied' ? 'info' : 'failure'}
        headingKey={
          initialReadFailure === 'denied'
            ? 'advisory:callout.denied.heading'
            : 'advisory:callout.failed.heading'
        }
        bodyKey={
          initialReadFailure === 'denied'
            ? 'advisory:errors.permissionDenied'
            : 'advisory:review.readFailed'
        }
        onRetry={initialReadFailure === 'failed' ? onReloadInitial : undefined}
      />
    );
  }
  const statement: AdvisoryReviewSegment[] = snapshot.statement.segments.map(segment =>
    segment.type === 'text'
      ? segment
      : { type: 'field', field: segment.field === 'period' ? 'reportingPeriod' : segment.field }
  );
  const problemFields = snapshot.errors.flatMap<AdvisoryReviewField>(({ field }) =>
    field === 'subjectCompany' || field === 'counterparties' || field === 'period'
      ? [viewField(field)]
      : []
  );
  const values = review.values;
  const fieldNotices = snapshot.errors.filter(error => {
    if (error.field === 'period') return false;
    const missingFieldAlreadyShown =
      error.code === 'required' &&
      ((error.field === 'subjectCompany' && !values.subjectCompany.trim()) ||
        (error.field === 'counterparties' &&
          Boolean(snapshot.counterpartyKind) &&
          values.counterparties.length === 0));
    return !missingFieldAlreadyShown;
  });
  const notices = (
    <Box sx={[contentColumn, { px: 2.5, pb: 3 }]}>
      {review.stale ? (
        <Alert severity="warning" data-testid="advisory-review-stale">
          {t('advisory:review.changedElsewhere')}
          <Box>
            <Button onClick={() => review.reconcile(false)}>
              {t('advisory:review.useSavedDraft')}
            </Button>
            <Button onClick={() => review.reconcile(true)}>
              {t('advisory:review.saveMyEdits')}
            </Button>
          </Box>
        </Alert>
      ) : null}
      {!review.error &&
        !readFailed &&
        !review.saving &&
        !review.busy &&
        !review.stale &&
        snapshot.status !== 'preparing' && (
          <Typography role="status" sx={[meta]}>
            {t('advisory:review.saved')}
          </Typography>
        )}
      <AdvisoryReviewRecoveryControls review={review} variant="draft" />
      {fieldNotices.length > 0 && (
        <Box role="status" aria-atomic="true" data-testid="advisory-review-field-issues">
          {fieldNotices.map((error, index) => (
            <AdvisoryReviewWarning key={`${error.field}-${index}`}>
              {t('advisory:review.fieldIssue', {
                field: t(`advisory:review.field.${error.field}`),
              })}
            </AdvisoryReviewWarning>
          ))}
        </Box>
      )}
    </Box>
  );
  return (
    <Box>
      <AdvisoryUnifiedReviewScreen
        key={review.scope}
        draft={{
          subjectCompany: review.values.subjectCompany,
          counterparties: review.values.counterparties,
          reportingPeriod: review.values.period,
        }}
        statement={statement}
        problemFields={problemFields}
        counterpartyKind={snapshot.counterpartyKind || null}
        feedback={review.feedback}
        onFeedbackChange={review.editFeedback}
        onEditStart={field => {
          editor.current = {
            field,
            base: {
              scope: review.scope,
              values: {
                ...values,
                counterparties: [...values.counterparties],
                period: { ...values.period },
              },
              counterpartyKind: snapshot.counterpartyKind,
            },
          };
        }}
        onEditCancel={() => {
          editor.current = null;
        }}
        onChange={(draft, field) => {
          const opened = editor.current;
          editor.current = null;
          if (!draft.reportingPeriod || opened?.field !== field) return;
          review.edit(
            {
              subjectCompany: draft.subjectCompany,
              counterparties: draft.counterparties,
              period: draft.reportingPeriod,
            },
            { field: field === 'reportingPeriod' ? 'period' : field, base: opened.base }
          );
        }}
        onRefine={review.refine}
        onConfirm={review.confirm}
        saving={review.saving}
        refining={review.busy === 'refine'}
        preparing={snapshot.status === 'preparing' || review.busy === 'prepare'}
        confirming={review.busy === 'confirm'}
        editingDisabled={review.confirmationPending}
        confirmationDisabled={!review.actionReady || !snapshot.canConfirm}
        refinementDisabled={!review.actionReady}
        canRefine={snapshot.canRefine}
        refinementsRemaining={snapshot.refinementsRemaining}
        periodState={
          snapshot.errors.some(e => e.field === 'period')
            ? 'failed'
            : snapshot.periodDefaulted
            ? 'defaulted'
            : 'found'
        }
        errorI18nKey={review.error}
        conflicts={snapshot.conflicts.map(conflict => ({
          field: viewField(conflict.field),
          mine: describe(conflict.manualValue),
          suggestion: describe(conflict.suggestedValue),
        }))}
        onResolveConflict={
          review.actionReady
            ? (field, choice) =>
                review.resolveConflict({
                  field: wireField(field),
                  decision: choice === 'keep' ? 'keepManual' : 'acceptSuggestion',
                })
            : undefined
        }
      />
      {notices}
    </Box>
  );
}
